/**
 * Centralized API error handling utilities.
 * Provides consistent error handling across all hooks, contexts, and API calls.
 */

import { toast } from "sonner";

export type ApiErrorSeverity = "silent" | "warn" | "error";

export interface ApiErrorOptions {
  /** Context label for logging (e.g. "fetching members") */
  context?: string;
  /** How to handle the error in the UI */
  severity?: ApiErrorSeverity;
  /** Custom message to show the user (overrides default) */
  userMessage?: string;
  /** Fallback value to return instead of throwing */
  fallback?: unknown;
}

/**
 * Determines if an error is a permission/auth error (403, RLS violation, etc.)
 */
export function isPermissionError(error: unknown): boolean {
  if (!error) return false;
  const message = getErrorMessage(error);
  const code = (error as any)?.code;
  const status = (error as any)?.status;
  
  return (
    status === 403 ||
    code === "42501" || // Postgres insufficient privilege
    code === "PGRST301" || // PostgREST insufficient privilege
    message.includes("permission denied") ||
    message.includes("row-level security") ||
    message.includes("new row violates row-level security") ||
    message.includes("403")
  );
}

/**
 * Determines if an error is a "not found" type error
 */
export function isNotFoundError(error: unknown): boolean {
  if (!error) return false;
  const code = (error as any)?.code;
  const status = (error as any)?.status;
  
  return status === 404 || code === "PGRST116";
}

/**
 * Determines if an error is a network/connectivity error
 */
export function isNetworkError(error: unknown): boolean {
  if (!error) return false;
  const message = getErrorMessage(error);
  
  return (
    message.includes("Failed to fetch") ||
    message.includes("NetworkError") ||
    message.includes("Network request failed") ||
    message.includes("ERR_NETWORK") ||
    message.includes("ERR_CONNECTION") ||
    message.includes("ECONNREFUSED")
  );
}

/**
 * Determines if an error is an auth/session error
 */
export function isAuthError(error: unknown): boolean {
  if (!error) return false;
  const message = getErrorMessage(error);
  const status = (error as any)?.status;
  
  return (
    status === 401 ||
    message.includes("JWT expired") ||
    message.includes("invalid token") ||
    message.includes("not authenticated") ||
    message.includes("Invalid Refresh Token")
  );
}

/**
 * Extract a user-friendly message from any error type
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null) {
    return (error as any).message || (error as any).error_description || JSON.stringify(error);
  }
  return "An unexpected error occurred";
}

/**
 * Get a user-friendly message based on error type
 */
function getUserFriendlyMessage(error: unknown): string {
  if (isPermissionError(error)) {
    return "You don't have permission to access this data.";
  }
  if (isAuthError(error)) {
    return "Your session has expired. Please sign in again.";
  }
  if (isNetworkError(error)) {
    return "Unable to connect. Please check your internet connection.";
  }
  if (isNotFoundError(error)) {
    return "The requested data was not found.";
  }
  return getErrorMessage(error);
}

/**
 * Handle an API error with consistent behavior.
 * Use this in contexts and non-React-Query async code.
 * 
 * @example
 * try {
 *   const { data, error } = await supabase.from("table").select("*");
 *   if (error) throw error;
 * } catch (err) {
 *   handleApiError(err, { context: "fetching table data", severity: "warn" });
 * }
 */
export function handleApiError(error: unknown, options: ApiErrorOptions = {}): void {
  const { context, severity = "warn", userMessage } = options;

  const logPrefix = context ? `[${context}]` : "[API Error]";
  const friendlyMessage = userMessage || getUserFriendlyMessage(error);

  // Always log
  if (isPermissionError(error)) {
    console.warn(`${logPrefix} Permission denied:`, getErrorMessage(error));
  } else if (isNetworkError(error)) {
    console.warn(`${logPrefix} Network error:`, getErrorMessage(error));
  } else {
    console.error(`${logPrefix}`, error);
  }

  // Show toast based on severity
  if (severity === "error") {
    toast.error(friendlyMessage);
  } else if (severity === "warn" && !isPermissionError(error)) {
    // Don't show toasts for permission errors (they're often expected for role-based access)
    toast.error(friendlyMessage);
  }
  // "silent" severity = no toast
}

/**
 * Wraps a Supabase query and returns the data or a fallback on error.
 * Ideal for non-critical data fetches that shouldn't crash the app.
 * 
 * @example
 * const invites = await safeQuery(
 *   () => supabase.from("invites").select("*").eq("org_id", orgId),
 *   { context: "fetching invites", fallback: [] }
 * );
 */
export async function safeQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: any }>,
  options: ApiErrorOptions & { fallback: T }
): Promise<T> {
  try {
    const { data, error } = await queryFn();
    if (error) {
      handleApiError(error, { ...options, severity: options.severity || "silent" });
      return options.fallback;
    }
    return data ?? options.fallback;
  } catch (err) {
    handleApiError(err, { ...options, severity: options.severity || "silent" });
    return options.fallback;
  }
}

/**
 * Global React Query error handler.
 * Called automatically by QueryClient for all query/mutation errors.
 */
export function globalQueryErrorHandler(error: unknown): void {
  // Permission errors are expected for role-based access - don't show toast
  if (isPermissionError(error)) {
    console.warn("[React Query] Permission denied:", getErrorMessage(error));
    return;
  }

  // Auth errors - session expired
  if (isAuthError(error)) {
    console.warn("[React Query] Auth error:", getErrorMessage(error));
    // Don't spam toasts - AuthContext handles session expiry
    return;
  }

  // Network errors
  if (isNetworkError(error)) {
    console.warn("[React Query] Network error:", getErrorMessage(error));
    return;
  }

  // Other errors - log but don't show toast (individual hooks handle their own toasts)
  console.error("[React Query] Query error:", error);
}
